import { rasterSrc } from "@/generated/raster-images";
import {
  createFileRoute,
  notFound,
  Link,
  redirect,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { asErrorComponent } from "@/lib/lazy-route";
import { getService } from "@/lib/services";
import { getServiceRedirectPath } from "@/lib/service-routes";
import {
  HeroSplit,
  TrustStrip,
  Section,
  SectionHeader,
  ProcessSteps,
  FAQAccordion,
  CrossSellBand,
  DiagnosticReferralBand,
  CTABand,
  BenefitsList,
  WhyChooseCRS,
} from "@/components/site-blocks";
import { EnquiryForm } from "@/components/enquiry-form";
import { PageBreadcrumb } from "@/components/page-breadcrumb";
import { breadcrumbJsonLdScript, servicesBreadcrumbTrail } from "@/lib/breadcrumbs";
import { pageSeo, serviceJsonLdScript } from "@/lib/seo";
import { SITE } from "@/lib/site";
import { Star } from "lucide-react";

export const Route = createFileRoute("/services/$slug")({
  beforeLoad: async ({ params }) => {
    const redirectPath = getServiceRedirectPath(params.slug);
    if (redirectPath) {
      throw redirect({ to: redirectPath, replace: true });
    }
  },
  loader: ({ params }) => {
    if (!getService(params.slug)) throw notFound();
    return null;
  },
  head: ({ params }) => {
    const slug = params?.slug ?? "";
    const s = getService(slug);
    if (!s) {
      const seo = pageSeo({
        path: `/services/${slug}`,
        title: "Service Not Found | CRS Energy",
        description:
          "Specialist insulation, damp, heating and renewables services across Wales and the South West.",
      });
      return {
        ...seo,
        meta: [...seo.meta, { name: "robots", content: "noindex, nofollow" }],
      };
    }
    return {
      ...pageSeo({
        path: `/services/${s.slug}`,
        title: `${s.name} | CRS Energy`,
        description: s.metaDescription,
        image: s.hero,
        ogType: "article",
      }),
      scripts: [
        serviceJsonLdScript({
          name: s.name,
          description: s.metaDescription,
          serviceType: s.category,
          url: `${SITE.url}/services/${s.slug}`,
          image: s.hero,
        }),
        breadcrumbJsonLdScript(servicesBreadcrumbTrail(s.name, `/services/${s.slug}`)),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: s.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  component: ServicePage,
  errorComponent: asErrorComponent(ServiceErrorComponent),
  notFoundComponent: ServiceNotFoundComponent,
});

function ServiceNotFoundComponent() {
  const { slug } = Route.useParams();
  return (
    <div className="container-page py-20 text-center">
      <h1 className="text-3xl md:text-4xl font-bold mb-3">Service not found</h1>
      <p className="text-muted-foreground mb-6">
        We couldn't find a service matching <code className="font-mono">{slug}</code>.
      </p>
      <Link
        to="/services"
        className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3 font-semibold hover:bg-accent/90 transition-colors"
      >
        Browse all services
      </Link>
    </div>
  );
}

function ServiceErrorComponent({ error }: ErrorComponentProps<Error>) {
  return (
    <div className="container-page py-20 text-center">
      <h1 className="text-3xl md:text-4xl font-bold mb-3">Something went wrong</h1>
      <p className="text-muted-foreground mb-6">{error.message}</p>
      <Link
        to="/services"
        className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3 font-semibold hover:bg-accent/90 transition-colors"
      >
        Back to services
      </Link>
    </div>
  );
}

function ServicePage() {
  // Resolve service from the URL param directly so the page renders even
  // if loader data hasn't dehydrated correctly during hydration.
  const { slug } = Route.useParams();
  const service = getService(slug);

  if (!service) {
    return <ServiceNotFoundComponent />;
  }

  return (
    <>
      <PageBreadcrumb
        items={servicesBreadcrumbTrail(service.name, `/services/${service.slug}`)}
      />
      <HeroSplit
        eyebrow={service.shortName}
        title={service.name}
        description={service.tagline}
        image={service.hero1280 ?? service.hero}
        imageAlt={service.scienceImageAlt}
        floatOverlayImages={[
          { src: service.hero800 ?? service.hero, slug: `${service.slug}-hero` },
          { src: service.scienceImage, slug: `${service.slug}-science` },
        ]}
        ctaPrimary={{ label: "Get a free quote", href: "#quote" }}
        ctaSecondary={{ label: "How it works", href: "#science" }}
        formSlot={
          <EnquiryForm
            formType="quote"
            variant="quote"
            defaultService={service.shortName}
            sourcePage={`/services/${service.slug}`}
            title="Get a free quote"
            subtitle="A member of our team will be in touch shortly to discuss your requirements."
          />
        }
      />

      <TrustStrip claims={service.trustClaims} />

      <Section id="science">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <SectionHeader eyebrow="The science" title={service.scienceTitle} />
            {service.scienceBody.map((p: string, i: number) => (
              <p key={i} className="text-muted-foreground mb-4 text-pretty">{p}</p>
            ))}
          </div>
          <img
            src={service.scienceImage}
            alt={service.scienceImageAlt}
            className="rounded-2xl shadow-soft w-full h-full max-h-[500px] object-cover"
            loading="lazy"
          />
        </div>
      </Section>

      <Section className="bg-card">
        <SectionHeader eyebrow="Why this matters" title={service.problemTitle} />
        <div className="grid md:grid-cols-2 gap-8 items-start">
          <ul className="space-y-3">
            {service.problemPoints.map((p: string) => (
              <li key={p} className="flex gap-3 items-start">
                <span className="mt-2 h-2 w-2 rounded-full bg-accent shrink-0" aria-hidden />
                <span>{p}</span>
              </li>
            ))}
          </ul>
          <div className="space-y-4">
            {service.intro.map((p: string, i: number) => (
              <p key={i} className="text-muted-foreground text-pretty">{p}</p>
            ))}
          </div>
        </div>
      </Section>

      <Section>
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <SectionHeader
              eyebrow="What you get"
              title={`Benefits of ${service.shortName.toLowerCase()}`}
            />
            <BenefitsList benefits={service.benefits} />
          </div>
          <div className="rounded-2xl bg-primary-soft border border-primary/10 p-7 md:p-9">
            <p className="text-xs font-semibold tracking-wider uppercase text-accent mb-3">
              CRS, you're in safe hands
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-primary leading-tight">
              Over 50 years of combined experience delivering this work to a higher standard.
            </h3>
            <div className="mt-5 flex gap-1">
              {[0, 1, 2, 3, 4].map((s) => (
                <Star key={s} className="h-5 w-5 fill-accent text-accent" aria-hidden />
              ))}
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              Trusted across Wales and the South West by homeowners, insurers and housing associations.
            </p>
          </div>
        </div>
      </Section>

      <Section className="bg-card">
        <SectionHeader
          eyebrow="Why choose CRS"
          title={`Why homeowners choose CRS for ${service.shortName.toLowerCase()}`}
        />
        <WhyChooseCRS />
      </Section>

      <Section>
        <SectionHeader eyebrow="Our process" title="Working with you every step of the way" />
        <ProcessSteps steps={service.processSteps} />
      </Section>

      <Section className="bg-primary text-primary-foreground rounded-none">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="flex gap-1 mb-3">
              {[0, 1, 2, 3, 4].map((s) => (
                <Star key={s} className="h-5 w-5 fill-accent text-accent" aria-hidden />
              ))}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-balance">Get the 5-star service</h2>
            <p className="mt-3 text-primary-foreground/85">
              From first call to final sign-off, our installers turn up on time, communicate clearly, and leave your property cleaner than they found it. That's why CRS keeps an outstanding customer service record across Wales and the South West.
            </p>
          </div>
          <img
            src={rasterSrc("stock/photo-1581094288338-2314dddb7ece")}
            alt="CRS installer on site"
            className="rounded-2xl w-full h-64 object-cover"
            loading="lazy"
          />
        </div>
      </Section>

      <Section className="bg-card">
        <SectionHeader eyebrow="FAQ" title="Frequently asked questions" />
        <FAQAccordion faqs={service.faqs} />
      </Section>

      <Section>
        <CrossSellBand slugs={service.crossSell} />
      </Section>

      <Section className="bg-card">
        <DiagnosticReferralBand brand={service.referralBrand} copy={service.referralCopy} />
      </Section>

      <Section id="quote">
        <div className="max-w-2xl mx-auto">
          <SectionHeader align="center" title="Get a quote today" description="Or call us on 0800 009 2992." />
          <EnquiryForm
            formType="quote"
            variant="quote"
            defaultService={service.shortName}
            sourcePage={`/services/${service.slug}#quote`}
          />
        </div>
      </Section>

      <CTABand title="Ready to get started?" />
    </>
  );
}
